import type { GhanttTimelineProps, GhanttTimelineScale } from './types';
import {
  ghanttAddCalendarMonths,
  ghanttParseTime,
  ghanttStartOfLocalMonth,
} from './ganttTimeline';

/** Inicio del rango global al hacer zoom con modificador (Cmd/Ctrl + rueda). */
export const GHANTT_MODIFIER_ZOOM_GLOBAL_START = '2015-01-01';

const MS_DAY = 86400000;
const MS_HOUR = 3600000;

/**
 * Mitad de la ventana en escala `hour` (zoom por modificador / línea local): total ≈ 2× ms.
 * Antes 24 h por lado (48 h); se sube a **100 h** por lado (**200 h** mínimo en cinta) para scroll + virtualización.
 */
export const GHANTT_MODIFIER_ZOOM_HOUR_HALF_MS = 100 * MS_HOUR;

/** Fin exclusivo del rango global: hasta el fin de 2030. */
export function ghanttModifierZoomGlobalEndExclusive(): string {
  return '2031-01-01';
}

/**
 * Orden de escala con zoom por modificador: de más detalle (índice 0) a más agregada (índice 3).
 * No incluye `year` (solo años en la rejilla): el tope es `month` (mes + año en cada etiqueta).
 */
export const GHANTT_MODIFIER_ZOOM_ORDER: readonly GhanttTimelineScale[] = [
  'hour',
  'day',
  'week',
  'month',
] as const;

/** Zoom mínimo de ventana (ms) — solo aplica a ventanas locales (p. ej. hora). */
export const GHANTT_MODIFIER_ZOOM_MIN_SPAN_MS = MS_HOUR;

/** Anchos por slot recomendados (px). */
export function ghanttDefaultColumnWidthForScale(scale: GhanttTimelineScale): number {
  switch (scale) {
    case 'hour':
      return 36;
    case 'day':
      /** Un poco más ancho por defecto: más aire por día en vista calendario. */
      return 46;
    case 'week':
      /** Misma anchura base que `day` (antes 112px): scroll y virtualización con ~el mismo nº de columnas visibles. */
      return 46;
    case 'month':
      return 72;
    case 'year':
      return 52;
    default:
      return 44;
  }
}

function toIsoDateLocal(ms: number): string {
  const d = new Date(ms);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Límite de ventana para zoom continuo: conserva hora local si no es medianoche (ventanas subdiarias). */
function toTimelineBoundString(ms: number): string {
  const d = new Date(ms);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const isMidnight =
    d.getHours() === 0 &&
    d.getMinutes() === 0 &&
    d.getSeconds() === 0 &&
    d.getMilliseconds() === 0;
  if (isMidnight) return `${y}-${mo}-${day}`;
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${y}-${mo}-${day}T${hh}:${mm}:${ss}`;
}

function clampWindow(vs: number, ve: number, g0: number, g1: number): { vs: number; ve: number } {
  const span = ve - vs;
  if (!(span > 0) || !Number.isFinite(span)) {
    return { vs: g0, ve: Math.min(g0 + MS_DAY, g1) };
  }
  if (vs < g0) {
    vs = g0;
    ve = vs + span;
  }
  if (ve > g1) {
    ve = g1;
    vs = ve - span;
  }
  if (vs < g0) vs = g0;
  if (ve <= vs) ve = Math.min(vs + MS_DAY, g1);
  return { vs, ve };
}

export function ghanttModifierZoomScaleIndex(scale: GhanttTimelineScale): number {
  const i = GHANTT_MODIFIER_ZOOM_ORDER.indexOf(scale);
  if (i >= 0) return i;
  /** `year` no está en el orden de zoom; se trata como el máximo agregado permitido (`month`). */
  if (scale === 'year') return GHANTT_MODIFIER_ZOOM_ORDER.length - 1;
  return 2;
}

/**
 * Escala según la **duración** visible (ms), con histéresis ligera (p. ej. API sin zoom por modificador).
 */
export function ghanttInferScaleForSpan(spanMs: number, prev?: GhanttTimelineScale): GhanttTimelineScale {
  const H = MS_HOUR;
  const D = MS_DAY;
  const ideal = (): GhanttTimelineScale => {
    if (spanMs <= 72 * H) return 'hour';
    if (spanMs <= 45 * D) return 'day';
    if (spanMs <= 120 * D) return 'week';
    if (spanMs <= 920 * D) return 'month';
    return 'month';
  };
  let out = ideal();
  if (!prev || prev === out) return out;

  if (prev === 'hour' && out === 'day' && spanMs < 72 * H * 1.22) out = 'hour';
  else if (prev === 'day' && out === 'hour' && spanMs > 72 * H / 1.22) out = 'day';
  else if (prev === 'day' && out === 'week' && spanMs < 45 * D * 1.12) out = 'day';
  else if (prev === 'week' && out === 'day' && spanMs > 45 * D / 1.12) out = 'week';
  else if (prev === 'week' && out === 'month' && spanMs < 120 * D * 1.1) out = 'week';
  else if (prev === 'month' && out === 'week' && spanMs > 120 * D / 1.1) out = 'month';

  return out;
}

/**
 * Construye `viewStart` / `viewEnd` centrados en `centerMs` y acotados al rango global (API genérica).
 * Para la línea completa con zoom por modificador usar {@link ghanttModifierZoomFullTimelineBounds}.
 */
export function ghanttModifierZoomWindowForScale(
  scale: GhanttTimelineScale,
  centerMs: number,
  globalStartMs: number,
  globalEndExclusiveMs: number,
): { viewStart: string; viewEnd: string } {
  if (scale === 'year') {
    return {
      viewStart: toTimelineBoundString(globalStartMs),
      viewEnd: toTimelineBoundString(globalEndExclusiveMs),
    };
  }

  if (scale === 'month') {
    const anchor = ghanttStartOfLocalMonth(centerMs);
    const vsMs = ghanttAddCalendarMonths(anchor, -9);
    const veMs = ghanttAddCalendarMonths(anchor, 9);
    const clamped = clampWindow(vsMs, veMs, globalStartMs, globalEndExclusiveMs);
    return {
      viewStart: toIsoDateLocal(clamped.vs),
      viewEnd: toIsoDateLocal(clamped.ve),
    };
  }

  const half =
    scale === 'hour'
      ? GHANTT_MODIFIER_ZOOM_HOUR_HALF_MS
      : scale === 'day'
        ? 10.5 * MS_DAY
        : scale === 'week'
          ? 42 * MS_DAY
          : 10.5 * MS_DAY;

  const clamped = clampWindow(centerMs - half, centerMs + half, globalStartMs, globalEndExclusiveMs);
  if (scale === 'hour') {
    return {
      viewStart: toTimelineBoundString(clamped.vs),
      viewEnd: toTimelineBoundString(clamped.ve),
    };
  }
  return {
    viewStart: toIsoDateLocal(clamped.vs),
    viewEnd: toIsoDateLocal(clamped.ve),
  };
}

/**
 * Ventana de timeline con **zoom por modificador**: casi siempre el rango global completo (2015 → fin
 * del año en curso) para que todos los años/períodos sigan presentes en la cinta; solo la **escala**
 * (rejilla) cambia. En **hora** se usa una ventana local centrada en `anchorMs` (no cabe la línea
 * entera a nivel hora en DOM). La ventana horaria total ≈ `2 * GHANTT_MODIFIER_ZOOM_HOUR_HALF_MS` (≥200 h).
 */
export function ghanttModifierZoomFullTimelineBounds(
  scale: GhanttTimelineScale,
  anchorMs: number,
  prev?: GhanttTimelineProps,
): { viewStart: string; viewEnd: string } {
  const g0 = prev?.modifierZoomGlobalStart
    ? ghanttParseTime(prev.modifierZoomGlobalStart)
    : ghanttParseTime(GHANTT_MODIFIER_ZOOM_GLOBAL_START);
  const g1 = prev?.modifierZoomGlobalEnd
    ? ghanttParseTime(prev.modifierZoomGlobalEnd)
    : ghanttParseTime(ghanttModifierZoomGlobalEndExclusive());

  if (!Number.isFinite(g0) || !Number.isFinite(g1) || g1 <= g0) {
    return {
      viewStart: GHANTT_MODIFIER_ZOOM_GLOBAL_START,
      viewEnd: ghanttModifierZoomGlobalEndExclusive(),
    };
  }

  if (scale === 'hour') {
    return ghanttModifierZoomWindowForScale('hour', anchorMs, g0, g1);
  }

  return {
    viewStart: toTimelineBoundString(g0),
    viewEnd: toTimelineBoundString(g1),
  };
}

/** Normaliza la línea de tiempo inicial cuando está activo `modifierWheelZoom`. */
export function ghanttModifierZoomNormalizeInitial(t: GhanttTimelineProps): GhanttTimelineProps {
  if (!t.modifierWheelZoom) {
    const globalEnd = ghanttModifierZoomGlobalEndExclusive();
    const g0 = ghanttParseTime(GHANTT_MODIFIER_ZOOM_GLOBAL_START);
    const g1 = ghanttParseTime(globalEnd);
    const hint = (t.scale ?? 'month') as GhanttTimelineScale;

    let viewStart = t.viewStart;
    let viewEnd = t.viewEnd;
    if (
      viewStart == null ||
      viewEnd == null ||
      !Number.isFinite(ghanttParseTime(viewStart)) ||
      !Number.isFinite(ghanttParseTime(viewEnd)) ||
      ghanttParseTime(viewEnd) <= ghanttParseTime(viewStart)
    ) {
      const anchor = ghanttStartOfLocalMonth(Date.now());
      const vsMs = ghanttAddCalendarMonths(anchor, -9);
      const veMs = ghanttAddCalendarMonths(anchor, 9);
      const clamped = clampWindow(vsMs, veMs, g0, g1);
      viewStart = toIsoDateLocal(clamped.vs);
      viewEnd = toIsoDateLocal(clamped.ve);
    }

    const vs = ghanttParseTime(viewStart);
    const ve = ghanttParseTime(viewEnd);
    const inferredRaw =
      Number.isFinite(vs) && Number.isFinite(ve) && ve > vs ? ghanttInferScaleForSpan(ve - vs, hint) : hint;
    const inferred = inferredRaw === 'year' ? 'month' : inferredRaw;

    return {
      ...t,
      viewStart,
      viewEnd,
      scale: inferred,
      columnWidthPx: t.columnWidthPx ?? ghanttDefaultColumnWidthForScale(inferred),
    };
  }

  const hintRaw = (t.scale ?? 'month') as GhanttTimelineScale;
  const hint = hintRaw === 'year' ? 'month' : hintRaw;
  const vs = ghanttParseTime(t.viewStart);
  const ve = ghanttParseTime(t.viewEnd);

  /** Sin `modifierZoomGlobal*`, el zoom con modificador usa 2015 → 1 ene (año+1), no `viewStart`/`viewEnd` (ventana inicial). */
  const defaultGlobalStartMs = ghanttParseTime(GHANTT_MODIFIER_ZOOM_GLOBAL_START);
  const defaultGlobalEndExclusiveMs = ghanttParseTime(ghanttModifierZoomGlobalEndExclusive());
  const g0 = t.modifierZoomGlobalStart ? ghanttParseTime(t.modifierZoomGlobalStart) : defaultGlobalStartMs;
  const g1 = t.modifierZoomGlobalEnd ? ghanttParseTime(t.modifierZoomGlobalEnd) : defaultGlobalEndExclusiveMs;

  const finalG0 = Number.isFinite(g0) ? g0 : defaultGlobalStartMs;
  const finalG1 = Number.isFinite(g1) ? g1 : defaultGlobalEndExclusiveMs;

  let anchorMs = (finalG0 + finalG1) / 2;
  if (Number.isFinite(vs) && Number.isFinite(ve) && ve > vs) {
    anchorMs = (vs + ve) / 2;
  }

  const { viewStart, viewEnd } = ghanttModifierZoomFullTimelineBounds(hint, anchorMs, {
    ...t,
    modifierZoomGlobalStart: toTimelineBoundString(finalG0),
    modifierZoomGlobalEnd: toTimelineBoundString(finalG1),
  });

  return {
    ...t,
    viewStart,
    viewEnd,
    scale: hint,
    columnWidthPx: t.columnWidthPx ?? ghanttDefaultColumnWidthForScale(hint),
    includeLeadingFullMonth: hint === 'day' ? false : t.includeLeadingFullMonth,
    modifierZoomGlobalStart: toTimelineBoundString(finalG0),
    modifierZoomGlobalEnd: toTimelineBoundString(finalG1),
  };
}

export interface GhanttModifierZoomWheelInput {
  /** Índice 0..3 en {@link GHANTT_MODIFIER_ZOOM_ORDER} (lo acumula `Ghantt` con la rueda). */
  targetScaleIndex: number;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  /** Fracción 0..1 sobre el contenido scrollable (instante bajo el puntero). */
  anchorFraction: number;
}

/** Límites de ancho de columna (px) para “zoom” dentro de la misma escala antes de cambiar de vista. */
export function ghanttModifierZoomColumnBounds(scale: GhanttTimelineScale): {
  min: number;
  max: number;
  defaultWidth: number;
} {
  const defaultWidth = ghanttDefaultColumnWidthForScale(scale);
  const min =
    scale === 'hour'
      ? Math.max(8, Math.round(defaultWidth * 0.30))
      : scale === 'day'
        ? Math.max(16, Math.round(defaultWidth * 0.40))
        : Math.max(18, Math.round(defaultWidth * 0.45));
  const max =
    scale === 'hour'
      ? Math.min(180, Math.round(defaultWidth * 4.0))
      : scale === 'day'
        ? Math.min(260, Math.round(defaultWidth * 5.0))
        : Math.min(210, Math.round(defaultWidth * 2.35));
  if (min >= max) {
    return { min: defaultWidth, max: defaultWidth + 1, defaultWidth };
  }
  return { min, max, defaultWidth };
}

/** Duracion en ms que representa un slot en cada escala. */
function ghanttScaleSlotDurationMs(scale: GhanttTimelineScale): number {
  switch (scale) {
    case 'hour':  return 3600000;
    case 'day':   return 86400000;
    case 'week':  return 7 * 86400000;
    case 'month': return 30.44 * 86400000;
    case 'year':  return 365.25 * 86400000;
    default:      return 86400000;
  }
}

/**
 * Calcula el columnWidthPx inicial de nextScale que preserva la misma densidad px/ms
 * que tenia currentWidthPx en currentScale. Forzado dentro de [min,max] de la escala destino.
 */
export function ghanttContinuousColumnWidth(
  currentScale: GhanttTimelineScale,
  currentWidthPx: number,
  nextScale: GhanttTimelineScale,
): number {
  const srcMs = ghanttScaleSlotDurationMs(currentScale);
  const dstMs = ghanttScaleSlotDurationMs(nextScale);
  const continuous = (currentWidthPx / srcMs) * dstMs;
  const { min, max } = ghanttModifierZoomColumnBounds(nextScale);
  return Math.max(min, Math.min(max, Math.round(continuous)));
}


function ghanttModifierZoomTimelineVisualEqual(a: GhanttTimelineProps, b: GhanttTimelineProps): boolean {
  return (
    (a.scale ?? 'day') === (b.scale ?? 'day') &&
    (a.columnWidthPx ?? -999) === (b.columnWidthPx ?? -999) &&
    String(a.viewStart) === String(b.viewStart) &&
    String(a.viewEnd) === String(b.viewEnd) &&
    (a.includeLeadingFullMonth !== false) === (b.includeLeadingFullMonth !== false)
  );
}

export interface GhanttModifierZoomWheelDeltaInput {
  /** Acumulado del batch (`>0` ≈ scroll “abajo” = zoom out / menos detalle). */
  deltaY: number;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  /** Fracción 0..1 del instante bajo el puntero (scroll tras zoom sigue usándola en `Ghantt`). */
  anchorFraction: number;
}

/**
 * Zoom con modificador vía rueda: primero **estrecha o ensancha columnas** en la escala actual;
 * solo al llegar al límite pasa al **siguiente nivel** de escala (p. ej. día → hora al acercar).
 */
export function ghanttModifierZoomWheelDelta(
  prev: GhanttTimelineProps,
  input: GhanttModifierZoomWheelDeltaInput,
): GhanttTimelineProps | null {
  if (prev.modifierWheelZoom !== true) return null;

  const raw = input.deltaY;
  if (raw === 0 || !Number.isFinite(raw)) return null;

  const zoomOut = raw > 0;
  const mag = Math.min(Math.abs(raw), 150);
  const ctrl = input.ctrlKey ? 1.12 : 1;
  /** Presupuesto en px de ancho de columna para este frame (varios ticks antes de agotar el rango). */
  let budget = Math.max(1, Math.round(mag * 0.7 * ctrl));
  /** Vista día: pasos un poco más finos → transición algo más larga con la misma rueda. */
  if ((prev.scale ?? 'day') === 'day') {
    budget = Math.max(1, Math.round(budget * 0.82));
  }

  let next: GhanttTimelineProps = { ...prev };

  const tryColumn = (): boolean => {
    const scale = (next.scale ?? 'day') as GhanttTimelineScale;
    const { min, max, defaultWidth } = ghanttModifierZoomColumnBounds(scale);
    const w = next.columnWidthPx ?? defaultWidth;
    if (!zoomOut && w < max) {
      const inc = Math.min(budget, max - w);
      if (inc <= 0) return false;
      next = { ...next, columnWidthPx: w + inc };
      budget -= inc;
      return true;
    }
    if (zoomOut && w > min) {
      const dec = Math.min(budget, w - min);
      if (dec <= 0) return false;
      next = { ...next, columnWidthPx: w - dec };
      budget -= dec;
      return true;
    }
    return false;
  };

  let guard = 0;
  while (budget > 0 && guard++ < 250) {
    if (tryColumn()) continue;
    const stepped = ghanttModifierZoomApplyStep(next, {
      direction: zoomOut ? 'out' : 'in',
      anchorFraction: input.anchorFraction,
    });
    if (!stepped || stepped.scale === next.scale) break;
    next = stepped;
    budget = Math.max(0, Math.floor(budget * 0.35) - 1);
  }

  if (ghanttModifierZoomTimelineVisualEqual(prev, next)) return null;
  return next;
}

function boundsKey(vs: string, ve: string): string {
  return `${vs}|${ve}`;
}

/**
 * Zoom por modificador: solo cambia la **escala** y mantiene la **cinta completa** (2015 → año actual)
 * salvo en vista **hora** (ventana local). El desplazamiento horizontal sigue el “enfoque” bajo el cursor.
 */
export function ghanttModifierZoomOnWheel(
  prev: GhanttTimelineProps,
  input: GhanttModifierZoomWheelInput,
): GhanttTimelineProps | null {
  const vs0 = ghanttParseTime(prev.viewStart);
  const ve0 = ghanttParseTime(prev.viewEnd);
  if (!Number.isFinite(vs0) || !Number.isFinite(ve0) || ve0 <= vs0) return null;

  const f = Math.max(0, Math.min(1, input.anchorFraction));
  const anchorMs = vs0 + f * (ve0 - vs0);

  const si = Math.max(0, Math.min(GHANTT_MODIFIER_ZOOM_ORDER.length - 1, Math.round(input.targetScaleIndex)));
  const scale = GHANTT_MODIFIER_ZOOM_ORDER[si];
  const { viewStart, viewEnd } = ghanttModifierZoomFullTimelineBounds(scale, anchorMs, prev);

  const prevScaleRaw = (prev.scale ?? 'day') as GhanttTimelineScale;
  const prevScale = prevScaleRaw === 'year' ? 'month' : prevScaleRaw;
  const columnWidthPx = ghanttDefaultColumnWidthForScale(scale);
  const prevColumnWidth = prev.columnWidthPx ?? ghanttDefaultColumnWidthForScale(prevScale);

  if (
    scale === prevScale &&
    columnWidthPx === prevColumnWidth &&
    boundsKey(viewStart, viewEnd) === boundsKey(String(prev.viewStart), String(prev.viewEnd))
  ) {
    if (!(scale === 'day' && prev.includeLeadingFullMonth !== false)) {
      return null;
    }
  }

  return {
    ...prev,
    viewStart,
    viewEnd,
    scale,
    columnWidthPx,
    includeLeadingFullMonth: scale === 'day' ? false : prev.includeLeadingFullMonth,
  };
}

export interface GhanttModifierZoomApplyStepOptions {
  direction: 'in' | 'out';
  anchorFraction?: number;
}

/**
 * Un paso **discreto** de escala (API opcional / pruebas).
 */
export function ghanttModifierZoomApplyStep(
  prev: GhanttTimelineProps,
  options: GhanttModifierZoomApplyStepOptions,
): GhanttTimelineProps | null {
  const direction = options.direction;
  const rawScale = (prev.scale ?? 'day') as GhanttTimelineScale;
  const scale = rawScale === 'year' ? 'month' : rawScale;
  const idx = GHANTT_MODIFIER_ZOOM_ORDER.indexOf(scale);
  if (idx < 0) return null;

  const nextIdx =
    direction === 'in' ? Math.max(0, idx - 1) : Math.min(GHANTT_MODIFIER_ZOOM_ORDER.length - 1, idx + 1);
  if (nextIdx === idx) return null;

  const nextScale = GHANTT_MODIFIER_ZOOM_ORDER[nextIdx];
  const vs = ghanttParseTime(prev.viewStart);
  const ve = ghanttParseTime(prev.viewEnd);
  
  const f = options.anchorFraction ?? 0.5;
  const anchorMs = Number.isFinite(vs) && Number.isFinite(ve) && ve > vs ? vs + f * (ve - vs) : NaN;
  
  const { viewStart, viewEnd } = ghanttModifierZoomFullTimelineBounds(
    nextScale,
    Number.isFinite(anchorMs)
      ? anchorMs
      : (ghanttParseTime(prev.modifierZoomGlobalStart ?? GHANTT_MODIFIER_ZOOM_GLOBAL_START) +
          ghanttParseTime(prev.modifierZoomGlobalEnd ?? ghanttModifierZoomGlobalEndExclusive())) /
        2,
    prev,
  );

  // Continuidad visual: el columnWidthPx inicial de la nueva escala preserva la densidad px/ms
  const currentWidth = prev.columnWidthPx ?? ghanttDefaultColumnWidthForScale(scale);
  const continuousWidth = ghanttContinuousColumnWidth(scale, currentWidth, nextScale);

  return {
    ...prev,
    scale: nextScale,
    viewStart,
    viewEnd,
    columnWidthPx: continuousWidth,
    includeLeadingFullMonth: nextScale === 'day' ? false : prev.includeLeadingFullMonth,
  };
}
